import type { Bible, CanonicalBook, SearchVerse } from '../types';

interface SearchResultsProps {
  results: SearchVerse[];
  query: string;
  bibles: Bible[];
  canonicalBooks: CanonicalBook[];
  onClear: () => void;
}

export default function SearchResults({ results, query, bibles, canonicalBooks, onClear }: SearchResultsProps) {
  // A search spans every translation, so the same verse comes back once per translation that matched.
  // Labelling each hit is what keeps those rows from reading as duplicates.
  const bookNames = new Map(canonicalBooks.map(b => [b.id, b.name]));
  const bibleNames = new Map(bibles.map(b => [b.id, b.name]));

  return (
    <div className="h-full overflow-y-auto flex items-start justify-center p-6">
      <div className="w-full" style={{ maxWidth: '1100px' }}>
        <main className="rounded-2xl shadow-lg p-8 bg-white dark:bg-ink-800">
          <div className="flex items-center justify-between mb-6 border-b border-gray-200 dark:border-ink-600 pb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-parchment-100">
                Search Results
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Found {results.length} result{results.length !== 1 ? 's' : ''} for &ldquo;{query}&rdquo;
              </p>
            </div>
            <button
              onClick={onClear}
              className="px-4 py-2 rounded-lg transition-colors font-medium border text-blue-600 border-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-400 dark:hover:bg-ink-700"
            >
              Back to Reading
            </button>
          </div>

          {results.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-12">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-ink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-lg">No verses found matching your search.</p>
              <p className="text-sm mt-2">Try different keywords or check the spelling.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {results.map((verse) => (
                <div key={verse.id} className="border-b border-gray-100 dark:border-ink-600 last:border-0 pb-3 last:pb-0">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-sm font-bold text-parchment-500 dark:text-parchment-300">
                      {bookNames.get(verse.canonicalBookId) ?? 'Unknown book'} {verse.chapterNumber}:{verse.verseNumber}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {bibleNames.get(verse.bibleTranslationId) ?? ''}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed mb-2 text-gray-700 dark:text-gray-300">
                    {verse.verseText}
                  </p>
                  {verse.commentCount && verse.commentCount > 0 && (
                    <p className="text-xs font-medium text-blue-500 dark:text-blue-400">
                      {verse.commentCount} comment{verse.commentCount > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
